import { useEffect, useState } from "react";
import { api, API } from "../lib/api";
import { Sun, Envelope, CalendarPlus, Sparkle, ArrowsClockwise, CheckCircle, WarningCircle, Clock } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function MorningBriefModule() {
  const [latest, setLatest] = useState(null);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);

  async function loadLatest() {
    try {
      const r = await api.get("/morning-brief/latest");
      setLatest(r.data);
    } catch (e) {
      // Aucun brief encore
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { loadLatest(); }, []);

  async function runScan() {
    setRunning(true);
    try {
      const r = await api.post("/morning-brief/run");
      toast.success(`📬 Scan terminé : ${r.data.meetings_detected} meeting(s) détecté(s) sur ${r.data.scanned_emails} emails`);
      await loadLatest();
    } catch (e) {
      const msg = e.response?.data?.detail || e.message;
      toast.error(`Échec du scan : ${msg}`);
    } finally {
      setRunning(false);
    }
  }

  function downloadIcs() {
    const token = localStorage.getItem("map_token");
    // Backend requires auth, but we open in new tab — easier path: fetch as blob then trigger download
    fetch(`${API}/morning-brief/calendar.ics`, {
      headers: { Authorization: `Bearer ${token}` },
    }).then(async (r) => {
      if (!r.ok) throw new Error("Download failed");
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "morning-brief-meetings.ics";
      a.click();
      URL.revokeObjectURL(url);
      toast.success("📅 .ics téléchargé · ouvre-le pour ajouter à ton calendrier");
    }).catch(() => toast.error("Impossible de télécharger l'ics"));
  }

  if (loading) return <div className="p-6 text-zinc-400">Chargement…</div>;

  const meetings = latest?.meetings || [];

  return (
    <div className="p-6 max-w-5xl mx-auto space-y-6" data-testid="morning-brief">
      <header className="flex items-start justify-between gap-4 border-b border-zinc-800 pb-4">
        <div>
          <div className="flex items-center gap-2 text-amber-400">
            <Sun size={22} weight="fill" />
            <span className="text-[10px] uppercase tracking-[0.3em] font-bold">Morning Brief · GPT-5.2</span>
          </div>
          <h1 className="text-3xl font-bold text-zinc-100 mt-1">Scan Gmail intelligent</h1>
          <p className="text-sm text-zinc-500 mt-1">
            Détection auto des meetings dans tes 48 dernières heures · brouillons de confirmation + .ics avec rappels
          </p>
        </div>
        <button
          onClick={runScan}
          disabled={running}
          data-testid="btn-run-scan"
          className="px-5 py-3 rounded-lg bg-amber-500 text-zinc-950 text-sm font-bold uppercase tracking-widest hover:bg-amber-400 disabled:opacity-50 disabled:cursor-not-allowed transition flex items-center gap-2"
        >
          {running ? <ArrowsClockwise size={16} className="animate-spin" /> : <Sparkle size={16} weight="fill" />}
          {running ? "Scan en cours…" : "Lancer le scan"}
        </button>
      </header>

      {!latest?.run_at && !running && (
        <div className="p-6 rounded-lg border border-dashed border-zinc-700 bg-zinc-900/40 text-center">
          <WarningCircle size={32} className="text-amber-400 mx-auto" />
          <h2 className="text-lg font-bold text-zinc-200 mt-2">Aucun scan effectué</h2>
          <p className="text-sm text-zinc-500 mt-1">
            Clique "Lancer le scan" pour analyser ta boîte de réception et extraire automatiquement les meetings.
          </p>
        </div>
      )}

      {latest?.run_at && (
        <div className="grid grid-cols-3 gap-4">
          <Stat icon={Envelope} label="Emails scannés" value={latest.scanned_emails || 0} accent="text-sky-400" testid="stat-scanned" />
          <Stat icon={CalendarPlus} label="Meetings détectés" value={meetings.length} accent="text-amber-400" testid="stat-meetings" />
          <Stat icon={CheckCircle} label="Brouillons créés" value={latest.drafts_created?.length || 0} accent="text-emerald-400" testid="stat-drafts" />
        </div>
      )}

      {latest?.run_at && (
        <div className="text-[11px] text-zinc-500 flex items-center gap-2">
          <Clock size={12} /> Dernier scan : {new Date(latest.run_at).toLocaleString("fr-CA")}
        </div>
      )}

      {meetings.length > 0 && (
        <section className="space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-sm font-bold text-zinc-300 uppercase tracking-widest">📅 Meetings extraits</h2>
            <button
              onClick={downloadIcs}
              data-testid="btn-download-ics"
              className="px-3 py-2 rounded bg-zinc-800 hover:bg-amber-600 hover:text-zinc-950 text-xs font-bold uppercase tracking-wider flex items-center gap-2 transition"
            >
              <CalendarPlus size={14} /> Télécharger .ics
            </button>
          </div>

          {meetings.map((m, i) => (
            <MeetingCard key={i} meeting={m} />
          ))}
        </section>
      )}

      {latest?.drafts_created?.length > 0 && (
        <section>
          <h2 className="text-sm font-bold text-zinc-300 uppercase tracking-widest mb-2">📬 Brouillons de confirmation Gmail</h2>
          <a
            href="https://mail.google.com/mail/u/impactkb.bjj@gmail.com/#drafts"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="link-gmail-drafts"
            className="inline-block px-4 py-2 rounded bg-amber-500/10 border border-amber-500/40 text-amber-300 text-xs uppercase tracking-widest font-bold hover:bg-amber-500 hover:text-zinc-950 transition"
          >
            Ouvrir Gmail Brouillons →
          </a>
          <ul className="mt-3 space-y-1.5 text-xs text-zinc-400">
            {latest.drafts_created.map((d, i) => (
              <li key={i} className="flex items-center gap-2">
                <CheckCircle size={12} className="text-emerald-400 shrink-0" />
                <span className="font-medium text-zinc-300">{d.title}</span>
                <span className="text-zinc-500">→ {d.to}</span>
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}

function Stat({ icon: Icon, label, value, accent, testid }) {
  return (
    <div data-testid={testid} className="p-4 rounded-lg bg-zinc-900/60 border border-zinc-800">
      <Icon size={18} className={accent} />
      <div className="mt-2 text-2xl font-bold text-zinc-100">{value}</div>
      <div className="text-[10px] uppercase tracking-widest text-zinc-500 mt-0.5">{label}</div>
    </div>
  );
}

function MeetingCard({ meeting }) {
  const isB2B = meeting.type === "b2b_reply" || !meeting.datetime_iso;
  const dt = meeting.datetime_iso ? new Date(meeting.datetime_iso) : null;
  const dtFormatted = dt ? dt.toLocaleString("fr-CA", {
    weekday: "long", year: "numeric", month: "long", day: "numeric",
    hour: "2-digit", minute: "2-digit",
  }) : null;

  return (
    <div data-testid="meeting-card" className={`p-4 rounded-lg border transition space-y-2 ${
      isB2B ? "bg-amber-950/20 border-amber-800/40 hover:border-amber-600/60" : "bg-zinc-900/70 border-zinc-800 hover:border-amber-700/50"
    }`}>
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <span className={`text-[9px] uppercase tracking-widest px-1.5 py-0.5 rounded font-bold ${
              isB2B ? "bg-amber-500/20 text-amber-300" : "bg-sky-500/20 text-sky-300"
            }`}>
              {isB2B ? "📬 RÉPONSE B2B" : "📅 MEETING"}
            </span>
            {dtFormatted && <span className="text-xs text-amber-400">{dtFormatted}</span>}
          </div>
          <h3 className="text-base font-bold text-zinc-100">{meeting.title || "Rendez-vous"}</h3>
        </div>
        {meeting.duration_min && (
          <span className="text-[10px] uppercase tracking-widest bg-amber-500/20 text-amber-300 px-2 py-1 rounded whitespace-nowrap">
            {meeting.duration_min} min
          </span>
        )}
      </div>
      {(meeting.with || meeting.email) && (
        <div className="text-xs text-zinc-400">
          👤 {meeting.with || "Contact"} {meeting.email ? `· ${meeting.email}` : ""}
        </div>
      )}
      {meeting.description && (
        <p className="text-sm text-zinc-400 line-clamp-3">{meeting.description}</p>
      )}
      {meeting.reply_draft && (
        <details className="text-xs" open={isB2B}>
          <summary className="cursor-pointer text-amber-400 hover:text-amber-300 uppercase tracking-widest font-bold">
            ✉️ Brouillon de réponse {isB2B ? "(prêt à envoyer)" : ""}
          </summary>
          <pre className="mt-2 p-3 bg-zinc-950 border border-zinc-800 rounded text-zinc-300 whitespace-pre-wrap font-sans">{meeting.reply_draft}</pre>
        </details>
      )}
    </div>
  );
}
